import React, { useEffect, useRef, useState } from 'react';
import { IPublicClientApplication } from '@azure/msal-browser';
import { Sprint } from '../../../types';
import {
  SprintRetrospective, getSprintRetrospective, createSprintRetrospective, updateSprintRetrospective,
} from '../../../services/retrospectiveService';
import { useCurrentUser } from '../../../hooks/useCurrentUser';
import { BMPrimaryButton, BM_LABEL, BM_TEXTAREA } from '../../../components/BoardModal';

interface Props {
  pca: IPublicClientApplication;
  sprint: Sprint;
  projectId: string;
  onClose: () => void;
}

/** Slide-in drawer for a sprint's retrospective. Create-or-view, with edit toggle. */
export default function RetrospectiveDrawer({ pca, sprint, projectId, onClose }: Props) {
  const [retro, setRetro] = useState<SprintRetrospective | null | undefined>(undefined); // undefined = loading
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ wentWell: '', didntGoWell: '', actionItems: '' });
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const flashTimer = useRef<number | null>(null);
  const me = useCurrentUser(pca, projectId);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await getSprintRetrospective(pca, sprint.dagrun_pm_sprintid);
        if (cancelled) return;
        setRetro(r);
        if (r) {
          setDraft({
            wentWell: r.dagrun_pm_wentwell ?? '',
            didntGoWell: r.dagrun_pm_didntgowell ?? '',
            actionItems: r.dagrun_pm_actionitems ?? '',
          });
        } else {
          setEditing(true); // open in edit mode when none exists yet
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Load failed');
      }
    })();
    return () => { cancelled = true; };
  }, [pca, sprint.dagrun_pm_sprintid]);

  const flash = () => {
    setSavedFlash(true);
    if (flashTimer.current) window.clearTimeout(flashTimer.current);
    flashTimer.current = window.setTimeout(() => setSavedFlash(false), 1500);
  };

  const handleSaveCreate = async () => {
    if (!draft.wentWell.trim() || !draft.didntGoWell.trim() || !me) return;
    setSaving(true); setError(null);
    try {
      await createSprintRetrospective(pca, {
        sprintId: sprint.dagrun_pm_sprintid,
        projectId,
        sprintName: sprint.dagrun_pm_name,
        facilitatedBy: me.displayName,
        wentWell: draft.wentWell.trim(),
        didntGoWell: draft.didntGoWell.trim(),
        actionItems: draft.actionItems.trim() || null,
      });
      const r = await getSprintRetrospective(pca, sprint.dagrun_pm_sprintid);
      setRetro(r);
      setEditing(false);
      flash();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally { setSaving(false); }
  };

  const handleEditPatch = async (patch: { wentWell?: string; didntGoWell?: string; actionItems?: string | null }) => {
    if (!retro) return;
    setSaving(true);
    try {
      await updateSprintRetrospective(pca, retro.dagrun_pm_sprintretrospectiveid, patch);
      setRetro(r => r ? {
        ...r,
        ...(patch.wentWell != null    ? { dagrun_pm_wentwell: patch.wentWell } : {}),
        ...(patch.didntGoWell != null ? { dagrun_pm_didntgowell: patch.didntGoWell } : {}),
        ...(patch.actionItems !== undefined ? { dagrun_pm_actionitems: patch.actionItems } : {}),
      } : r);
      flash();
    } finally { setSaving(false); }
  };

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,17,23,0.18)', zIndex: 100 }} />
      <div style={{
        position: 'fixed', top: 0, right: 0, height: '100%', width: 480,
        background: '#fff', borderLeft: '0.5px solid #EAECF0',
        boxShadow: '0 4px 24px rgba(15,17,23,0.12)', zIndex: 101,
        overflowY: 'auto', padding: '16px 24px 32px 24px',
        fontFamily: 'var(--font-ui)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <span style={{ fontSize: 11, color: '#9198A8', opacity: savedFlash ? 1 : 0, transition: 'opacity 0.2s' }}>{savedFlash ? 'Saved ✓' : ''}</span>
          <div style={{ display: 'flex', gap: 6 }}>
            {retro && !editing && (
              <button onClick={() => setEditing(true)}
                style={{ background: 'transparent', border: '1px solid #DDE0E6', color: '#5A6272', padding: '4px 12px', borderRadius: 6, fontFamily: 'var(--font-ui)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
              >Edit</button>
            )}
            <button onClick={onClose} aria-label="Close" style={{ width: 28, height: 28, border: 'none', background: 'transparent', cursor: 'pointer', color: '#5A6272', fontSize: 18 }}>×</button>
          </div>
        </div>

        <div style={{ fontSize: 18, fontWeight: 600, color: '#0F1117', marginBottom: 4 }}>
          {sprint.dagrun_pm_name} — Retrospective
        </div>
        {retro && (
          <div style={{ fontSize: 12, color: '#9198A8', marginBottom: 16 }}>
            Facilitated by {retro.dagrun_pm_facilitatedby ?? '—'}
            {retro.dagrun_pm_retrodate && ` · ${new Date(retro.dagrun_pm_retrodate).toLocaleDateString()}`}
          </div>
        )}

        {retro === undefined && <div style={{ color: '#9198A8' }}>Loading…</div>}

        {(retro === null || editing) && (
          <>
            <Section label="Went Well *">
              <textarea value={draft.wentWell} onChange={e => setDraft(d => ({ ...d, wentWell: e.target.value }))}
                onBlur={() => retro ? handleEditPatch({ wentWell: draft.wentWell }) : null}
                style={BM_TEXTAREA} placeholder="What worked?" />
            </Section>
            <Section label="Didn't Go Well *">
              <textarea value={draft.didntGoWell} onChange={e => setDraft(d => ({ ...d, didntGoWell: e.target.value }))}
                onBlur={() => retro ? handleEditPatch({ didntGoWell: draft.didntGoWell }) : null}
                style={BM_TEXTAREA} placeholder="What didn't work?" />
            </Section>
            <Section label="Action Items">
              <textarea value={draft.actionItems} onChange={e => setDraft(d => ({ ...d, actionItems: e.target.value }))}
                onBlur={() => retro ? handleEditPatch({ actionItems: draft.actionItems || null }) : null}
                style={BM_TEXTAREA} placeholder="List action items, one per line." />
            </Section>

            {!retro && (
              <div style={{ marginTop: 18 }}>
                <BMPrimaryButton onClick={handleSaveCreate} disabled={saving || !draft.wentWell.trim() || !draft.didntGoWell.trim() || !me}>
                  {saving ? 'Saving…' : 'Save Retrospective'}
                </BMPrimaryButton>
              </div>
            )}
            {error && <div style={{ marginTop: 12, color: '#DC2626', fontSize: 12 }}>{error}</div>}
          </>
        )}

        {retro && !editing && (
          <>
            <ReadEyebrow>Went Well</ReadEyebrow>
            <ReadBody>{retro.dagrun_pm_wentwell ?? '—'}</ReadBody>
            <ReadEyebrow>Didn't Go Well</ReadEyebrow>
            <ReadBody>{retro.dagrun_pm_didntgowell ?? '—'}</ReadBody>
            <ReadEyebrow>Action Items</ReadEyebrow>
            <ReadBody>{retro.dagrun_pm_actionitems ?? '—'}</ReadBody>
          </>
        )}
      </div>
    </>
  );
}

const Section = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div style={{ marginBottom: 14 }}>
    <label style={BM_LABEL}>{label}</label>
    {children}
  </div>
);
const ReadEyebrow = ({ children }: { children: React.ReactNode }) => (
  <div style={{ fontSize: 11, fontWeight: 600, color: '#5A6272', textTransform: 'uppercase', letterSpacing: '0.12em', marginTop: 16, marginBottom: 6, paddingTop: 12, borderTop: '0.5px solid #EAECF0' }}>{children}</div>
);
const ReadBody = ({ children }: { children: React.ReactNode }) => (
  <div style={{ fontSize: 14, color: '#2C313D', lineHeight: 1.55, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{children}</div>
);
